import React, { useState } from 'react';
import {
   Box,
   Typography,
   Paper,
   Tabs,
   Tab,
   Divider,
   Alert,
   Snackbar,
} from '@mui/material';
import {
   AutoFixHigh as AIIcon,
   PhotoLibrary as GalleryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import PhotoSelector from './components/PhotoSelector';
import TransformationPanel from './components/TransformationPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import { SelectedPhoto } from './types';
import MyTransformedGallery from './components/MyTransformedGallery';
import { getAuthHeaders } from '../../../utils/auth';

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;
   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`photo-studio-tabpanel-${index}`}
         aria-labelledby={`photo-studio-tab-${index}`}
         {...other}
         style={{ width: '100%' }}
      >
         {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
   );
}

const AIPhotoStudioPage: React.FC = () => {
   const [tabIndex, setTabIndex] = useState(0);
   const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
   const [error] = useState<string | null>(null);
   const { currentUser } = useAuth();
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'info'>('info');

   const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue);
   };

   const handleSavePhoto = async (photo: {
      id: string;
      originalPhotoId: string;
      transformedUrl: string;
      type: string;
      tripId?: string;
      eventId?: string;
      userId?: string;
   }) => {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/photos/save-enhanced`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  enhancedUrl: photo.transformedUrl,
                  imageId: photo.id,
                  originalPhotoId: photo.originalPhotoId,
                  originalUrl: '',
               }),
            }
         );

         if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to save');
         }

         setSnackbarMessage('Photo saved to My Photos');
         setSnackbarSeverity('info');
         setSnackbarOpen(true);
      } catch {
         setSnackbarMessage('Error saving photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const legacySavePhoto = async (photo: any) => {
      await handleSavePhoto({
         id: photo.id,
         originalPhotoId: photo.originalPhotoId || photo.id,
         transformedUrl: photo.transformedUrl,
         type: photo.type,
         tripId: photo.tripId,
         eventId: photo.eventId,
         userId: photo.userId,
      });
   };

   return (
      <Box sx={{ width: '100%', px: { xs: 1, sm: 2 }, mt: 2, mb: 4 }}>
         <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant='h4' component='h1' gutterBottom>
               <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
               AI Photo Studio
            </Typography>
            <Typography variant='body1' color='text.secondary' paragraph>
               Enhance your photos with AI-powered professional quality improvements.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
               <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  aria-label='photo studio tabs'
                  variant='fullWidth'
               >
                  <Tab
                     icon={<AIIcon />}
                     label='Enhance Photos'
                     id='photo-studio-tab-0'
                     aria-controls='photo-studio-tabpanel-0'
                  />
                  <Tab
                     icon={<GalleryIcon />}
                     label='My Enhancements'
                     id='photo-studio-tab-1'
                     aria-controls='photo-studio-tabpanel-1'
                  />
               </Tabs>
            </Box>
         </Paper>

         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         <TabPanel value={tabIndex} index={0}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
               <Box sx={{ width: { xs: '100%', md: '40%' } }}>
                  <PhotoSelector
                     onPhotoSelect={(photos) => {
                        setSelectedPhotos(photos.length > 0 ? photos : []);
                     }}
                     selectedPhotos={selectedPhotos}
                  />
               </Box>

               <Divider
                  orientation='vertical'
                  flexItem
                  sx={{ display: { xs: 'none', md: 'block' } }}
               />

               <Box sx={{ width: { xs: '100%', md: '60%' } }}>
                  <TransformationPanel
                     selectedPhotos={selectedPhotos}
                     onSavePhoto={handleSavePhoto}
                  />
               </Box>
            </Box>
         </TabPanel>

         <TabPanel value={tabIndex} index={1}>
            <Typography variant='h6' gutterBottom>
               My Enhanced Photos
            </Typography>
            <MyTransformedGallery savePhoto={legacySavePhoto} />
         </TabPanel>

         <TabPanel value={tabIndex} index={2}>
            <AnalyticsPanel />
         </TabPanel>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default AIPhotoStudioPage;
